import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../store/collectionsStore';
import { CollectionCard } from '../components/CollectionCard';
import { CollectionEditorModal } from '../components/CollectionEditorModal';
import { ShareCollectionModal } from '../components/ShareCollectionModal';
import type { CollectionWithPois, CollectionVisibility } from '@poi-planner/shared';

export function CollectionsPage() {
  const navigate = useNavigate();
  const {
    collections,
    loading,
    loadCollections,
    createCollection,
    updateCollection,
    deleteCollection,
  } = useCollectionsStore();

  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<CollectionWithPois | null>(null);
  const [shareCollectionId, setShareCollectionId] = useState<string | null>(null);

  // Load collections on mount
  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const handleCreate = () => {
    setEditingCollection(null);
    setShowEditorModal(true);
  };

  const handleEdit = (collection: CollectionWithPois) => {
    setEditingCollection(collection);
    setShowEditorModal(true);
  };

  const handleSave = async (data: { name: string; description?: string; visibility: CollectionVisibility }) => {
    if (editingCollection) {
      await updateCollection(editingCollection.id, data);
    } else {
      await createCollection(data.name, data.description, data.visibility);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteCollection(id);
  };

  const handleShare = (collectionId: string) => {
    setShareCollectionId(collectionId);
  };

  const shareCollection = collections.find((c) => c.id === shareCollectionId);

  return (
    <div className="min-h-screen bg-kanban-bg">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-xl">arrow_back</span>
            </button>
            <div className="flex items-center gap-2">
              <svg
                width="28"
                height="28"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z"
                  fill="#111418"
                />
              </svg>
              <span className="text-lg font-bold text-text-primary tracking-tight">
                Collections
              </span>
            </div>
          </div>

          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-text-primary text-white rounded-xl text-sm font-semibold border-none cursor-pointer transition-all hover:opacity-90 hover:shadow-lg hover:shadow-slate-300/30"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            New Collection
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Section header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary mb-1">My Collections</h1>
          <p className="text-sm text-text-secondary">
            {collections.length === 0
              ? 'Organize your POIs into collections for easy sharing'
              : `${collections.length} ${collections.length === 1 ? 'collection' : 'collections'}`}
          </p>
        </div>

        {/* Collections grid or empty state */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-3 border-gray-200 border-t-amber-500 rounded-full animate-spin mb-4"></div>
            <span className="text-sm text-text-secondary">Loading collections...</span>
          </div>
        ) : collections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="flex items-center justify-center size-20 rounded-2xl bg-amber-100 mb-6">
              <span className="material-symbols-outlined text-4xl text-amber-600">
                folder_special
              </span>
            </div>
            <h2 className="text-lg font-bold text-text-primary mb-2">No collections yet</h2>
            <p className="text-sm text-text-secondary mb-6 text-center max-w-sm">
              Create collections to organize your POIs. Group recommendations by theme, source, or
              share them with others.
            </p>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2.5 bg-text-primary text-white rounded-xl text-sm font-semibold border-none cursor-pointer transition-all hover:opacity-90"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              Create Your First Collection
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {collections.map((collection) => (
              <CollectionCard
                key={collection.id}
                collection={collection}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onShare={handleShare}
              />
            ))}

            {/* Add collection placeholder card */}
            <button
              onClick={handleCreate}
              className="group flex flex-col items-center justify-center min-h-[180px] rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 cursor-pointer transition-all hover:border-slate-400 hover:bg-slate-100/50"
            >
              <div className="flex items-center justify-center size-12 rounded-xl bg-white border border-slate-200 mb-3 transition-all group-hover:shadow-md group-hover:scale-105">
                <span className="material-symbols-outlined text-2xl text-slate-400 group-hover:text-slate-600">
                  add
                </span>
              </div>
              <span className="text-sm font-medium text-slate-500 group-hover:text-slate-700">
                New Collection
              </span>
            </button>
          </div>
        )}
      </main>

      <CollectionEditorModal
        open={showEditorModal}
        onClose={() => {
          setShowEditorModal(false);
          setEditingCollection(null);
        }}
        collection={editingCollection}
        onSave={handleSave}
      />

      {shareCollection && (
        <ShareCollectionModal
          isOpen={!!shareCollectionId}
          onClose={() => setShareCollectionId(null)}
          collectionId={shareCollection.id}
          collectionName={shareCollection.name}
          isOwner={shareCollection.isOwner}
        />
      )}
    </div>
  );
}
